import './JobSettings.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode } from 'react';
import { Loader } from '../../../../__legacy__/components/Loader';
import { ValidatedBooleanParameter } from '../../../../__legacy__/components/ValidatedBooleanParameter';
import { ValidatedNumericParameter } from '../../../../__legacy__/components/ValidatedNumericParameter';
import { mapSchemeToForm, Parameter, PARAMETER_TYPE_INTERNAL, Parameters } from '../../../../__legacy__/helpers/parameters.helpers';
import { ACDLError } from '../../../../__legacy__/models/error.model';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { IJobDetailsStore } from '../../../jobs/jobDetails.store';

interface IJobSettingsProps {
  parameters: Parameters;
  validationErrors: IJobDetailsStore['validationErrors'];
  onChangeNumericParam: (label: string) => (value: string) => void;
  onBlurNumericParam: (label: string) => (value: string) => void;
  onChangeBooleanParam: (label: string) => () => void;
  settings: IJobDetailsStore['job']['config'];
  isFormDisabled: boolean;
  isLoading: boolean;
}

export const JobSettings = observer((props: IJobSettingsProps): ReactElement => {
  const { parameters, isFormDisabled, settings, validationErrors, onChangeNumericParam, onBlurNumericParam, onChangeBooleanParam, isLoading } = props;
  const { showAll, settings: settingsErrors } = validationErrors;
  const { t } = useTranslator();

  const createFormField = (item: Parameter, key: number): ReactNode => {
    const type = item.type;
    switch (type) {
      case PARAMETER_TYPE_INTERNAL.INT:
      case PARAMETER_TYPE_INTERNAL.FLOAT: {
        const { name } = item;
        const validationError = settingsErrors.get(name);

        return (
          <ValidatedNumericParameter
            key={name}
            parameter={item}
            fieldIndex={key}
            disabled={isFormDisabled}
            onBlur={onBlurNumericParam(name)}
            onChange={onChangeNumericParam(name)}
            value={settings[name]?.toString() || ''}
            alwaysShowError={showAll}
            error={validationError}
          />
        );
      }
      case PARAMETER_TYPE_INTERNAL.BOOL: {
        const { name } = item;

        return (
          <ValidatedBooleanParameter
            key={name}
            parameter={item}
            fieldIndex={key}
            disabled={isFormDisabled}
            onChange={onChangeBooleanParam(name)}
            value={(settings[name] as boolean) || false}
          />
        );
      }
      // Will create compilation errors in case any form field isn't handled
      default: {
        const exhaustiveCheck: never = type;
        throw new ACDLError(`Unhandled form field: ${exhaustiveCheck}`);
      }
    }
  };

  const leftParameters = mapSchemeToForm(parameters, createFormField);
  const rightParameters = mapSchemeToForm(parameters, createFormField, true);
  const className = 'card-with-list job-settings-form';

  if (isLoading) {
    return (
      <S_TabsCard className={className}>
        <Loader isLoading={true} />
      </S_TabsCard>
    );
  }

  return (
    <S_TabsCard className='card-with-list job-settings-form'>
      <h4>{t('parameters')}</h4>
      {rightParameters.length === 0 && leftParameters.length === 0 ? (
        <div>{t('no_parameters')}</div>
      ) : (
        <div className='job-settings-form-columns'>
          <div className='job-settings-form-column'>{leftParameters}</div>
          <div className='job-settings-form-column'>{rightParameters}</div>
        </div>
      )}
    </S_TabsCard>
  );
});
